import * as vp from 'vopay';
import moment from 'moment';
import { FunctionComponent, JSX, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { TransferFundRequest } from '../types';

import { useOpenModule } from '../../../../providers/OpenModule.provider';
import { useNavMenu } from '../../../../providers/NavMenu.provider';
import usePostTransactionsHistory from '../../../../hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, Title, SubTitle, Button, Badge, Description } = vp;

type FC = FunctionComponent;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type ID = {
  ClientAccountID: string;
  ID: string;
};

type DataProps = {
  WithdrawTransactionID: string;
  WithdrawTransactionIDs: Record<string, ID>;
  AccountTransferID: string;
  AccountTransferIDs: Record<string, ID>;
  DebitorClientAccount: string;
  RecipientClientAccount: string;
  Country: string;
  values: TransferFundRequest;
};

const WithdrawTransferDetails: FC = () => {
  const { onSubNavClick } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const { state } = useLocation<DataProps>();
  const [transactions, getTransactions] = usePostTransactionsHistory({});
  const [transferIds, setTransferIds] = useState<string[]>([]);
  const [withdrawIds, setWithdrawIds] = useState<string[]>([]);
  const history = useHistory();

  useEffect(() => {
    if (
      (state.WithdrawTransactionID || state.WithdrawTransactionIDs) &&
      (state.AccountTransferID || state.AccountTransferIDs)
    ) {
      const transactionIDs = [];

      if (state.AccountTransferID !== '') {
        transactionIDs.push(state.AccountTransferID);
        setTransferIds([state.AccountTransferID]);
      } else if (Object.keys(state.AccountTransferIDs).length !== 0) {
        const ids = Object.values(state.AccountTransferIDs).map((item: ID) => item.ID);
        setTransferIds(ids);
        transactionIDs.push(...ids);
      }

      if (state.WithdrawTransactionID !== '') {
        transactionIDs.push(state.WithdrawTransactionID);
        setWithdrawIds([state.WithdrawTransactionID]);
      } else if (Object.keys(state.WithdrawTransactionIDs).length !== 0) {
        const ids = Object.values(state.WithdrawTransactionIDs).map((item: ID) => item.ID);
        setWithdrawIds(ids);
        transactionIDs.push(...ids);
      }

      getTransactions({
        StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
        EndDateTime: moment().utc().format('YYYY-MM-DD'),
        TransactionID: transactionIDs.join(','),
      });
    }
  }, []);

  return (
    <>
      <Box mb="md">
        <Title title="Transfer and Withdraw Overview" />
      </Box>

      <Box pt="md" pb="md">
        <SubTitle subTitle="Transfer" />
      </Box>
      {Object.values(transactions.Transactions)
        .filter((subTransaction: vp.TransactionsLists) => transferIds.includes(subTransaction.TransactionID))
        .map((subTransaction: vp.TransactionsLists) => (
          <Box key={subTransaction.TransactionID} mt="md" flx="col">
            <Description bold>Transaction Details - {getRecipientName(subTransaction.TransactionID)}</Description>
            <Table {...createTransferDetails(subTransaction)} />
          </Box>
        ))}

      <Box pt="md" pb="md">
        <SubTitle subTitle="Withdraw" />
      </Box>
      {Object.values(transactions.Transactions)
        .filter((subTransaction: vp.TransactionsLists) => withdrawIds.includes(subTransaction.TransactionID))
        .map((subTransaction: vp.TransactionsLists) => (
          <Box key={subTransaction.TransactionID} mt="md" flx="col">
            <Description bold>
              Transaction Details - {getWithdrawRecipientName(subTransaction.TransactionID)}
            </Description>
            <Table {...createWithdrawDetails(subTransaction)} />
          </Box>
        ))}
    </>
  );

  function createTransferDetails(subTransaction: vp.TransactionsLists): vp.TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: 'Transaction ID',
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label="Details"
              color="secondary"
              size="sm"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', 'Transactions History');
                history.push({
                  pathname: `/transactions-history/details/AccountTransfer/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: 'Type',
        value: 'Account Transfer',
      },
      {
        display: 'Debitor Client Account',
        value: state.DebitorClientAccount,
      },
      {
        display: 'Recipient Client Account',
        value: getRecipientName(subTransaction.TransactionID),
      },
      {
        display: 'Amount',
        value: `$${subTransaction.DebitAmount}`,
      },
      {
        display: 'Currency',
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: vp.TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: 'Transaction Detail' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }

  function getWithdrawRecipientName(transactionID: string): string {
    const result = Object.values(transactions.Transactions).find(
      (subTransaction: vp.TransactionsLists) => subTransaction.TransactionID === transactionID,
    );
    return result ? result.FullName : 'Recipient client Name Not Found';
  }

  function getRecipientName(transactionID: string): string {
    const result = Object.values(transactions.Transactions).find(
      (subTransaction: vp.TransactionsLists) => subTransaction.ParentTransactionID === transactionID,
    );
    return result ? result.FullName : 'Recipient client Name Not Found';
  }

  function createWithdrawDetails(subTransaction: vp.TransactionsLists): vp.TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: 'Transaction ID',
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label="Details"
              color="secondary"
              size="sm"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', 'Transactions History');
                history.push({
                  pathname: `/transactions-history/details/EFTWithdraw/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: 'Transaction Status',
        value: <Badge label={vp.capitalizeFirstLetter('Pending')} hasIcon color={vp.GetColorStatus('pending')} />,
      },
      {
        display: 'Transaction Date',
        value: moment(subTransaction.TransactionDateTime).format('YYYY-MM-DD'),
      },
      {
        display: 'Last Modified',
        value: moment(subTransaction.LastModified).format('YYYY-MM-DD'),
      },
      {
        display: 'Type',
        value: 'Withdraw',
      },
      {
        display: 'Amount',
        value: `$${subTransaction.DebitAmount}`,
      },
      {
        display: 'Currency',
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: vp.TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: 'Transaction Detail' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }
};

export default WithdrawTransferDetails;
